import {useCallback, useEffect, useRef, useState} from 'react';

import {useLocale} from '../../../provider/locale/locale-hook';
import {LangKeyType} from '../../../provider/locale/translation/type';
import {UseCompaniesSelectorHookType} from '../../../service/feature-companies/feature-companies-type';
import {
    StoriesCompanySelectorCompanyType,
    YandexStoryFormFieldsEnum,
    YandexStoryFormType,
    YandexStoryTypeEnum,
} from '../../../service/yandex-stories/yandex-stories-type';
import {Form} from '../../../typings/antd';

import {YandexStoryFormContentStep} from './slides-content-steps/content-step/yandex-story-form-content-step';
import {YandexStoriesSelectCompaniesStep} from './slides-content-steps/select-companies-step/yandex-stories-select-companies-step';

type PropsType = {
    onSubmit: (values: YandexStoryFormType) => Promise<void>;
    onChangeStep: (width: number) => void;
    onChangeTitle: (titleLangKey: LangKeyType) => void;
    closeModal: () => void;
    companyId?: number;
    mode: 'edit' | 'create';
    type: YandexStoryTypeEnum;
    companiesSelector: UseCompaniesSelectorHookType<StoriesCompanySelectorCompanyType>;
    initialValues?: Partial<YandexStoryFormType>;
    brandId?: number;
};

export function YandexStoryForm(props: PropsType): JSX.Element {
    const {
        onSubmit,
        closeModal,
        companyId,
        mode,
        type,
        companiesSelector,
        initialValues,
        onChangeStep,
        onChangeTitle,
        brandId,
    } = props;

    const {getLocalizedString} = useLocale();

    const [form] = Form.useForm<YandexStoryFormType>();

    const [currentStep, setCurrentStep] = useState<number>(0);

    const [selectedBrandId, setSelectedBrandId] = useState<number | null>(brandId || null);

    const {selectCompany, selectorId, isInProgress, selectedCount} = companiesSelector;

    const hasCompaniesSelectorStep = type === YandexStoryTypeEnum.Story;
    const isDirtyRef = useRef<boolean>(false);

    useEffect(() => {
        if (companyId && mode === 'create' && selectorId && !isInProgress && !isDirtyRef.current) {
            isDirtyRef.current = true;

            selectCompany(companyId);
        }
    }, [companyId, mode, selectCompany, selectorId, isInProgress]);

    useEffect(() => {
        if (brandId) {
            setSelectedBrandId(brandId);
        }

        if (selectedCount === 0 && !brandId) {
            setSelectedBrandId(null);
        }
    }, [brandId, selectedCount, setSelectedBrandId]);

    useEffect(() => {
        form.setFieldsValue({[YandexStoryFormFieldsEnum.CompaniesSelector]: selectorId});
    }, [form, selectorId]);

    // The company selection step is a picker of its own and carries its own modal title.
    useEffect(() => {
        if (hasCompaniesSelectorStep && currentStep === 0) {
            onChangeTitle('YANDEX_STORY__SELECT_COMPANIES__TITLE');
            return;
        }

        onChangeTitle(mode === 'edit' ? 'YANDEX_STORY__EDIT__TITLE' : 'YANDEX_STORY__CREATE__TITLE');
    }, [currentStep, hasCompaniesSelectorStep, mode, onChangeTitle]);

    const goToNextStep = useCallback(() => {
        setCurrentStep((previousState) => previousState + 1);
    }, []);

    const goToPreviousStep = useCallback(() => {
        if (currentStep === 0) {
            closeModal();
            return;
        }

        setCurrentStep((previousState) => previousState - 1);
    }, [currentStep, closeModal]);

    const handleFormSubmit = useCallback(
        async (values: YandexStoryFormType) => {
            await onSubmit(values);
            closeModal();
        },
        [closeModal, onSubmit]
    );

    const formSteps = [
        ...(hasCompaniesSelectorStep
            ? [
                  <YandexStoriesSelectCompaniesStep
                      companiesSelector={companiesSelector}
                      formInstance={form}
                      key="companiesStep"
                      onStepActivated={onChangeStep}
                      onStepFinished={goToNextStep}
                      selectedBrandId={selectedBrandId}
                      setSelectedBrandId={brandId ? null : setSelectedBrandId}
                  />,
              ]
            : []),
        <YandexStoryFormContentStep
            formInstance={form}
            formMode={mode}
            isFirstStep={!hasCompaniesSelectorStep}
            key="contentStep"
            onPreviousStep={goToPreviousStep}
            onStepActivated={onChangeStep}
            onSubmit={handleFormSubmit}
        />,
    ];

    return (
        <Form<YandexStoryFormType>
            form={form}
            initialValues={{
                ...(type === YandexStoryTypeEnum.Navigation
                    ? {
                          [YandexStoryFormFieldsEnum.Name]: getLocalizedString('YANDEX_STORY__HOW_TO_FIND__NAME'),
                      }
                    : {}),
                [YandexStoryFormFieldsEnum.AutoAddCompanies]: false,
                // The type declares `slides` as required and `getFieldValue` is typed accordingly, so
                // the field has to start as an empty map: the add-slide uploader reads the current
                // count before appending, and on a fresh story it is the first thing that touches it.
                [YandexStoryFormFieldsEnum.Slides]: {},
                type,
                ...initialValues,
            }}
            layout="vertical"
        >
            {formSteps && formSteps[currentStep]}
            <Form.Item<YandexStoryFormType> hidden name={YandexStoryFormFieldsEnum.Type} />
            <Form.Item<YandexStoryFormType> hidden name={YandexStoryFormFieldsEnum.CompaniesSelector} />
            <Form.Item<YandexStoryFormType> hidden name={YandexStoryFormFieldsEnum.AutoAddCompanies} />
        </Form>
    );
}
